import moment from 'moment-timezone';
import {
  DeskConfirmationStatus,
  GetProfileBookingsQuery,
} from 'generated/graphql';
import { resolveZone } from './formatBookings';
import { getCheckInStart } from './checkInWindow';

export type ProfileReservation = NonNullable<
  GetProfileBookingsQuery['getDeskReservationsByUserIds'][number]
>['reservations'][number];

export type ReservationKind = 'booking' | 'assignment';

export type ReservationMenuAction =
  | 'copy'
  | 'edit'
  | 'reportIssue'
  | 'release'
  | 'unassign'
  | 'cancel';

export type CheckInStatus =
  | 'notRequired'
  | 'localOnly'
  | 'required'
  | 'checkedIn'
  | 'none';

export type ReservationActions = {
  kind: ReservationKind;
  menuActions: ReservationMenuAction[];
  showCheckIn: boolean;
  isCheckedIn: boolean;
  checkInStatus: CheckInStatus;
};

const SEATS_DELEGATE = 'seats:delegate';

const ALLOW_SEAT_CHECK_INS = 'allow-seat-check-ins-feature';
const ASSIGNED_RESERVATION_CHECK_INS_ENABLED =
  'assigned-reservation-check-ins-enabled';
const ENFORCE_LOCAL_CHECK_IN_ONLY = 'enforce-local-check-in-only';

type SettingValue = string | boolean | null | undefined;

const isTrueSetting = (value: SettingValue): boolean =>
  value === true || value === 'true';

const getSettingValue = (
  reservation: ProfileReservation,
  slug: string
): SettingValue =>
  reservation.seat.settings?.find((setting) => setting?.slug === slug)?.value;

const computeAllowCheckIns = (
  reservation: ProfileReservation,
  kind: ReservationKind
): boolean => {
  const allowSeatCheckInsSetting = getSettingValue(
    reservation,
    ALLOW_SEAT_CHECK_INS
  );
  const assignedCheckInsSetting =
    reservation.seat.reservationPolicies?.checkinPolicies
      ?.assignedCheckInEnabled ??
    getSettingValue(reservation, ASSIGNED_RESERVATION_CHECK_INS_ENABLED);

  if (!isTrueSetting(allowSeatCheckInsSetting)) return false;
  if (kind === 'assignment') return isTrueSetting(assignedCheckInsSetting);
  return true;
};

const hasSeatPermission = (
  reservation: ProfileReservation,
  name: string
): boolean =>
  reservation.seat.permissions.some(
    (permission) => permission.name === name && permission.value === true
  );

// Reservation types the backend treats as long-lived assignments. Mirrors the
// legacy dashboard, which splits the profile desk list on the reservation
// `type` (assigned/shared) rather than the desk's configured types. This is
// what a `type` such as `hoteled`, `hot`, or `reverse_hotel` returns instead —
// note `reverse_hotel` sits on an *assigned* desk (so `seat.rawType` includes
// `assigned`), which is exactly why classifying on the desk type mislabeled it.
const ASSIGNMENT_RESERVATION_TYPES = new Set(['assigned', 'shared']);

export const getReservationKind = (
  reservation: ProfileReservation
): ReservationKind =>
  ASSIGNMENT_RESERVATION_TYPES.has(reservation.type.toLowerCase())
    ? 'assignment'
    : 'booking';

export const isAssignedReservationReleasedToday = (
  reservation: ProfileReservation,
  now: moment.Moment = moment()
): boolean => {
  if (getReservationKind(reservation) !== 'assignment') {
    return false;
  }

  const zone = resolveZone(
    reservation.timeZone ?? reservation.seat.location.timezone
  );
  return reservation.exclusions.some((exclusion) =>
    moment.tz(exclusion.startTime, zone).isSame(now, 'day')
  );
};

export const getReservationActions = (
  reservation: ProfileReservation,
  isCurrentPerson: boolean,
  canReportIssue = false,
  now: moment.Moment = moment()
): ReservationActions => {
  const kind = getReservationKind(reservation);
  const canDelegate = hasSeatPermission(reservation, SEATS_DELEGATE);
  const canActOnReservation = isCurrentPerson || canDelegate;
  const allowExclusions =
    reservation.seat.reservationPolicies?.allowExclusions === true;

  const enforceLocalCheckInOnly =
    reservation.seat.reservationPolicies?.checkinPolicies
      ?.enforceLocalCheckinOnly === true ||
    isTrueSetting(getSettingValue(reservation, ENFORCE_LOCAL_CHECK_IN_ONLY));
  const allowCheckIns = computeAllowCheckIns(reservation, kind);
  const isReleasedAssignmentToday = isAssignedReservationReleasedToday(
    reservation,
    now
  );

  const isCheckedIn =
    !isReleasedAssignmentToday &&
    allowCheckIns &&
    reservation.confirmation?.status === DeskConfirmationStatus.Confirmed;

  const checkInStart = getCheckInStart(reservation, kind);
  const withinCheckInWindow =
    reservation.canCheckInNow === true ||
    (checkInStart !== null && checkInStart.isBefore(now));
  const showCheckIn =
    canActOnReservation &&
    !isCheckedIn &&
    !isReleasedAssignmentToday &&
    allowCheckIns &&
    !enforceLocalCheckInOnly &&
    withinCheckInWindow;

  const checkInStatus: CheckInStatus = isReleasedAssignmentToday
    ? 'none'
    : isCheckedIn
    ? 'checkedIn'
    : !allowCheckIns
    ? 'notRequired'
    : enforceLocalCheckInOnly
    ? 'localOnly'
    : showCheckIn
    ? 'none'
    : 'required';

  const menuActions: ReservationMenuAction[] = ['copy'];

  if (canReportIssue) {
    menuActions.push('reportIssue');
  }

  if (kind === 'booking' && canActOnReservation) {
    menuActions.push('edit', 'cancel');
  }

  if (
    kind === 'assignment' &&
    canActOnReservation &&
    allowExclusions &&
    !!reservation.seriesId
  ) {
    menuActions.push('release');
  }

  if (kind === 'assignment' && canActOnReservation) {
    menuActions.push('unassign');
  }

  return {
    kind,
    menuActions,
    showCheckIn,
    isCheckedIn,
    checkInStatus,
  };
};

export const isReservationActiveToday = (
  reservation: ProfileReservation,
  now: moment.Moment = moment()
): boolean => {
  if (getReservationKind(reservation) === 'assignment') {
    return true;
  }

  if (!reservation.endTime) {
    return true;
  }
  const zone = resolveZone(
    reservation.timeZone ?? reservation.seat.location.timezone
  );
  return moment.tz(reservation.endTime, zone).isAfter(now);
};

const KIND_ORDER: Record<ReservationKind, number> = {
  assignment: 0,
  booking: 1,
};

const compareReservations = (
  a: ProfileReservation,
  b: ProfileReservation
): number => {
  const byKind =
    KIND_ORDER[getReservationKind(a)] - KIND_ORDER[getReservationKind(b)];
  if (byKind !== 0) {
    return byKind;
  }
  const startA = a.startTime ?? '';
  const startB = b.startTime ?? '';
  if (startA !== startB) {
    return startA < startB ? -1 : 1;
  }
  const byName = a.seat.name.localeCompare(b.seat.name, undefined, {
    numeric: true,
  });
  if (byName !== 0) {
    return byName;
  }
  return a.id.localeCompare(b.id);
};

// The bookings query window is the viewer's local day, so an assignment on a
// desk in another timezone can return two series instances (e.g. an EST
// viewer's day overlaps two PST desk days). Keep the first instance per
// series, matching the legacy profile page's first-match behavior.
const dedupeAssignmentSeries = (
  reservations: ProfileReservation[]
): ProfileReservation[] => {
  const seenSeriesIds = new Set<string>();
  return reservations.filter((reservation) => {
    if (
      getReservationKind(reservation) !== 'assignment' ||
      !reservation.seriesId
    ) {
      return true;
    }
    if (seenSeriesIds.has(reservation.seriesId)) {
      return false;
    }
    seenSeriesIds.add(reservation.seriesId);
    return true;
  });
};

export const getActiveReservations = (
  reservations: ProfileReservation[],
  now: moment.Moment = moment()
): ProfileReservation[] =>
  dedupeAssignmentSeries(
    reservations
      .filter((reservation) => isReservationActiveToday(reservation, now))
      .sort(compareReservations)
  );
